// Tolerant "open now" reader for posted hours — handles OSM opening_hours ("Mo-Fr 07:00-18:00") and
// human text ("Tue–Sat 11am–6pm · Sun 12–5pm · Closed Mon"). Returns null when it can't tell.
const DAYS = ["su", "mo", "tu", "we", "th", "fr", "sa"];
const DAY_RX = /\b(sun|mon|tue|wed|thu|fri|sat|su|mo|tu|we|th|fr|sa)[a-z]*\b/gi;
const dayIdx = (w) => DAYS.indexOf(w.slice(0, 2).toLowerCase());
const RANGE_WORDS = /\b(daily|every ?day|everyday|7 days|all week|open daily)\b/i;
const TIME_RX = /(\d{1,2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.|a|p|noon|midnight)?\s*(?:–|—|-|to|until|till)\s*(\d{1,2}(?::\d{2})?\s*(?:am|pm|a\.m\.|p\.m\.|a|p)?|noon|midnight|close|closing|late)/i;

const toHours = (h, m, suffix, fallback) => {
  let hh = Number(h), mm = Number(m || 0);
  const s = (suffix || "").replace(/\./g, "").toLowerCase();
  const is24h = !s && m !== undefined && /^\d{2}$/.test(h); // "06:00", "14:00" — OSM-style 24-hour clock
  if (s === "noon") return 12;
  if (s === "midnight") return 24;
  if (s.startsWith("p") && hh < 12) hh += 12;
  else if (s.startsWith("a") && hh === 12) hh = 0;
  else if (!s && !is24h && fallback) hh = fallback(hh);
  return hh + mm / 60;
};

const parseDays = (seg) => {
  if (RANGE_WORDS.test(seg)) return [0, 1, 2, 3, 4, 5, 6];
  const tokens = [...seg.matchAll(DAY_RX)].map((m) => ({ i: dayIdx(m[0]), at: m.index, len: m[0].length })).filter((t) => t.i >= 0);
  if (!tokens.length) return null;
  const out = new Set();
  for (let k = 0; k < tokens.length; k++) {
    const t = tokens[k], next = tokens[k + 1];
    const between = next ? seg.slice(t.at + t.len, next.at) : "";
    if (next && /^\s*(–|—|-|to|through|thru)\s*$/i.test(between)) {
      for (let d = t.i; ; d = (d + 1) % 7) { out.add(d); if (d === next.i) break; }
      k++;
    } else out.add(t.i);
  }
  return [...out];
};

const parseTimes = (seg) => {
  if (/24\s*\/\s*7|24 hours|open 24/i.test(seg)) return [[0, 24]];
  const all = [...seg.matchAll(new RegExp(TIME_RX.source, "gi"))].map(parseRange);
  return all.length ? all : null;
};

const parseRange = (m) => {
  const endRaw = m[4].toLowerCase();
  let end;
  if (/close|closing|late/.test(endRaw)) end = 26;
  else if (endRaw === "midnight") end = 24;
  else if (endRaw === "noon") end = 12;
  else {
    const em = endRaw.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.|a|p)?/);
    end = toHours(em[1], em[2], em[3], (h) => (h <= 7 ? h + 12 : h)); // bare "6" at the end of a range means 6pm
  }
  const endSuffix = (m[4].match(/am|pm|a\.m\.|p\.m\.|\b[ap]\b/i) || [""])[0].replace(/\./g, "").toLowerCase();
  const start = toHours(m[1], m[2], m[3], (h) => {
    // "5–10 PM" → start shares the pm; "11–2am" → 11am (previous half-day); "4p-close" handled by suffix
    if (endSuffix.startsWith("p") && h <= end % 24 && h !== 12) return h + 12;
    if (h <= 6) return h + 12;
    return h;
  });
  if (end <= start) end += 24;
  return [start, end];
};

export const parseHours = (text) => {
  if (!text) return null;
  const clean = text.replace(/\s+/g, " ").trim();
  if (/call to confirm|every other|varies|by appointment/i.test(clean) && !TIME_RX.test(clean)) return null;
  const segs = clean.split(/\s*(?:·|;|\||\n|\u2022|,(?=\s*(?:mon|tue|wed|thu|fri|sat|sun|mo|tu|we|th|fr|sa|daily)\b))\s*/i).filter(Boolean);
  const table = Array.from({ length: 7 }, () => []);
  let touched = false, lastDays = null;
  for (const seg of segs) {
    if (/every other/i.test(seg)) continue;
    const days = parseDays(seg);
    if (/\bclosed\b/i.test(seg)) { if (days) { days.forEach((d) => { table[d] = []; }); touched = true; } continue; }
    const times = parseTimes(seg);
    if (!times) continue;
    const apply = days || lastDays || [0, 1, 2, 3, 4, 5, 6];
    apply.forEach((d) => table[d].push(...times));
    if (days) lastDays = days;
    touched = true;
  }
  return touched ? table : null;
};

const fmt = (h) => {
  const hh = ((h % 24) + 24) % 24, mm = Math.round((hh % 1) * 60);
  if (hh === 0 && !mm) return "midnight";
  if (hh === 12 && !mm) return "noon";
  const h12 = Math.floor(hh) % 12 || 12;
  return `${h12}${mm ? `:${String(mm).padStart(2, "0")}` : ""}${hh < 12 ? "am" : "pm"}`;
};

export const openStatus = (text, now = new Date()) => {
  const table = parseHours(text);
  if (!table) return null;
  const local = new Date(now.toLocaleString("en-US", { timeZone: "America/Los_Angeles" }));
  const day = local.getDay(), t = local.getHours() + local.getMinutes() / 60;
  const spill = table[(day + 6) % 7].filter(([, e]) => e > 24).map(([s, e]) => [s - 24, e - 24]);
  const today = [...spill, ...table[day]].sort((a, b) => a[0] - b[0]);
  const open = today.find(([s, e]) => t >= s && t < e);
  if (open && open[0] <= 0 && open[1] >= 24) return { status: "open", closes: 24, label: "Open 24 hours", short: "open 24h" };
  if (open) return { status: "open", closes: open[1], label: open[1] >= 26 ? "Open now · till late" : `Open now · till ${fmt(open[1])}`, short: open[1] >= 26 ? "open" : `till ${fmt(open[1])}` };
  const next = today.find(([s]) => s > t);
  if (next) return { status: "closed", opens: next[0], label: `Closed · opens ${fmt(next[0])}`, short: `opens ${fmt(next[0])}` };
  for (let k = 1; k <= 7; k++) {
    const d = (day + k) % 7;
    if (table[d].length) {
      const s = Math.min(...table[d].map(([a]) => a));
      const name = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d];
      return { status: "closed", label: `Closed today · opens ${k === 1 ? "tomorrow" : name} ${fmt(s)}`, short: `opens ${k === 1 ? "tmrw" : name} ${fmt(s)}` };
    }
  }
  return { status: "closed", label: "Closed", short: "closed" };
};

const parseCloses = (text) => {
  const t = (text || "").trim().toLowerCase();
  if (!t) return null;
  if (/midnight|12\s*am/.test(t)) return 24;
  if (/close|late|varies/.test(t)) return null;
  const m = t.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm|a|p)?/);
  if (!m) return null;
  let h = Number(m[1]) + Number(m[2] || 0) / 60;
  const s = m[3] || "";
  if (s.startsWith("p") && h < 12) h += 12;
  else if (!s && h <= 6) h += 24;         // "2" → 2am next morning
  else if (!s && h < 12) h += 12;         // "9" → 9pm
  else if (s.startsWith("a") && h < 12) h += 24;
  return h;
};

// Night map: confidence-aware status. Strongest evidence first: a show on right now → admin closes-at (✓ if verified
// in the last 120 days) → listed hours (~, may be stale). Returns null when we simply don't know (no badge).
export const nightStatus = ({ closes, hours, verifiedAt, events = [] }, now = new Date()) => {
  const local = new Date(now.toLocaleString("en-US", { timeZone: "America/Los_Angeles" }));
  const t = local.getHours() + local.getMinutes() / 60; // hours since local midnight today
  const yesterday = new Date(local.getTime() - 864e5).toDateString();
  const todays = events.map((ev) => {
    const d = new Date(ev.start_datetime);
    if (Number.isNaN(d.getTime())) return null;
    const dl = new Date(d.toLocaleString("en-US", { timeZone: "America/Los_Angeles" }));
    const s = dl.getHours() + dl.getMinutes() / 60;
    if (dl.toDateString() === local.toDateString()) return { s, e: s + 3, label: ev.local_time };
    if (dl.toDateString() === yesterday) return { s: s - 24, e: s - 21, label: ev.local_time }; // last night's show, still running past midnight
    return null;
  }).filter(Boolean);
  const live = todays.find((ev) => t >= ev.s && t < ev.e);
  if (live) return { status: "open", confidence: "show", label: "Show on now", short: "● show on now", note: "A show is on right now — that's as good as hours get." };
  const upcoming = todays.find((ev) => ev.s > t);
  const end = parseCloses(closes); // e.g. 26 = 2am after midnight
  const fresh = verifiedAt && (now - new Date(verifiedAt)) / 864e5 <= 120;
  if (end != null) {
    const lastNightEnd = end - 24;
    if (t < 12 && t < lastNightEnd) return { status: "open", confidence: fresh ? "verified" : "listing", label: `Open till ${fmt(end)}`, short: `${fresh ? "✓ " : "~"}till ${fmt(end)}`, note: fresh ? `Closing time verified by the venue ${new Date(verifiedAt).toLocaleDateString()}.` : "Closing time from the venue, not re-checked lately — confirm before you go." };
    if (t >= 16 && t < end) return { status: "open", confidence: fresh ? "verified" : "listing", label: `Open till ${fmt(end)}`, short: `${fresh ? "✓ " : "~"}till ${fmt(end)}`, note: fresh ? `Closing time verified by the venue ${new Date(verifiedAt).toLocaleDateString()}.` : "Closing time from the venue, not re-checked lately — confirm before you go." };
    if (t < 12 && t >= lastNightEnd && !upcoming) return { status: "closed", confidence: fresh ? "verified" : "listing", label: `Closed · last call was ${fmt(end)}`, short: `${fresh ? "" : "~"}closed`, note: "" };
  }
  if (upcoming) return { status: "closed", confidence: "show", label: `Show at ${upcoming.label}`, short: `show ${upcoming.label.replace(":00", "").replace(" ", "")}`, note: "Doors usually open before the show — check their page." };
  const st = openStatus(hours, now);
  if (st) return { ...st, confidence: "listing", short: `~${st.short}`, note: "From their listed hours — listings go stale; check before you go." };
  return null;
};
